// Ultimate Card Game (UCG) - Portrait Overview Strip
//
// In portrait, the camera only shows a ~29% slice of the 1600px lane at a
// time (see js/ui/viewport.js's 'cover-height' mode) - this strip is the
// only place the rest of the battle is visible at all. Tap or drag it to
// jump the camera anywhere on the lane; dragging on the field itself still
// works too (Viewport.panBy), this is just a faster, whole-lane-at-a-glance
// way to get there. Entirely dormant (both CSS-hidden and a render() no-op)
// outside portrait - see the .minimap-canvas media query in css/main.css.
export class Minimap {
  constructor(game) {
    this.game = game;
    this.canvas = document.getElementById('minimap-canvas');
    this.ctx = this.canvas?.getContext('2d') || null;

    this._cachedW = 0;
    this._cachedH = 0;
    this._sizeDirty = true;
    this._lastRenderTime = -1000;

    if (this.canvas) {
      this._bindInput();
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', () => { this._sizeDirty = true; });
        window.addEventListener('orientationchange', () => { this._sizeDirty = true; });
      }
    }
  }

  invalidateSize() {
    this._sizeDirty = true;
  }

  _bindInput() {
    let dragging = false;
    const jumpFromClientX = (clientX) => {
      const rect = this.canvas.getBoundingClientRect();
      if (rect.width <= 0) return;
      const t = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      this.game.viewport.jumpTo(t * this.game.battlefield.width);
    };
    this.canvas.addEventListener('pointerdown', (e) => {
      dragging = true;
      // The jump itself must happen even if capture fails for some reason
      // (it can throw in rare edge cases) - losing the ability to drag
      // past the canvas's own bounds is a much smaller loss than the tap
      // not moving the camera at all.
      jumpFromClientX(e.clientX);
      try { this.canvas.setPointerCapture(e.pointerId); } catch { /* best-effort */ }
    });
    this.canvas.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      jumpFromClientX(e.clientX);
    });
    const stop = () => { dragging = false; };
    this.canvas.addEventListener('pointerup', stop);
    this.canvas.addEventListener('pointercancel', stop);
  }

  render() {
    if (!this.canvas || !this.ctx) return;
    const viewport = this.game.viewport;
    // Camera mode is the source of truth for "are we in portrait" (see
    // Viewport.resize) - cheaper and always in sync vs. re-deriving it
    // from window dimensions again here.
    if (viewport.mode !== 'cover-height') return;

    // 1. Throttle tactical overview strip to ~20 FPS (50ms interval)
    // Minimap is a low-res 10px strip; 20 FPS is completely fluid and saves 66% GPU/CPU overhead.
    const now = performance.now();
    if (now - this._lastRenderTime < 50) return;
    this._lastRenderTime = now;

    // 2. Cache canvas dimensions to eliminate forced synchronous layouts (Reflows)
    if (this._sizeDirty || this._cachedW <= 0) {
      this._cachedW = this.canvas.clientWidth;
      this._cachedH = this.canvas.clientHeight;
      this._sizeDirty = false;

      if (this._cachedW > 0 && this._cachedH > 0) {
        const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
        const bw = Math.round(this._cachedW * dpr);
        const bh = Math.round(this._cachedH * dpr);
        if (this.canvas.width !== bw || this.canvas.height !== bh) {
          this.canvas.width = bw;
          this.canvas.height = bh;
        }
      }
    }

    const w = this._cachedW;
    const h = this._cachedH;
    if (w < 1 || h < 1) return;

    const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const battlefield = this.game.battlefield;
    const worldW = battlefield.width;
    const scaleX = w / worldW;

    // Territory tint, same read as the in-field territory rail: which side
    // currently controls more of the lane.
    const dividerX = (battlefield.territoryDivider ?? worldW / 2) * scaleX;
    ctx.fillStyle = 'rgba(0, 229, 255, 0.12)';
    ctx.fillRect(0, 0, dividerX, h);
    ctx.fillStyle = 'rgba(255, 51, 68, 0.12)';
    ctx.fillRect(dividerX, 0, w - dividerX, h);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.fillRect(Math.round(dividerX) - 0.5, 0, 1, h);

    // Base end caps - brighter the healthier that base still is (zero-string allocation)
    const baseW = battlefield.bases?.western;
    const baseE = battlefield.bases?.eastern;
    if (baseW && baseW.hp > 0) {
      const ratio = Math.max(0.15, baseW.hp / baseW.maxHp);
      ctx.fillStyle = '#38bdf8';
      ctx.globalAlpha = 0.4 + 0.6 * ratio;
      ctx.fillRect(0, 0, 4, h);
    }
    if (baseE && baseE.hp > 0) {
      const ratio = Math.max(0.15, baseE.hp / baseE.maxHp);
      ctx.fillStyle = '#ff4466';
      ctx.globalAlpha = 0.4 + 0.6 * ratio;
      ctx.fillRect(w - 4, 0, 4, h);
    }
    ctx.globalAlpha = 1.0;

    // Unit blips - batched by team to eliminate Canvas2D state thrashing (Paradigma 6)
    const units = battlefield.units;
    const len = units.length;
    const blipY = Math.round(h / 2 - 1.5);

    // Team Western blips
    ctx.fillStyle = '#7dd3fc';
    for (let i = 0; i < len; i++) {
      const u = units[i];
      if (u.isDead || u.team !== 'western') continue;
      ctx.fillRect(Math.round(u.x * scaleX) - 1, blipY, 2, 3);
    }

    // Team Eastern blips
    ctx.fillStyle = '#ff8095';
    for (let i = 0; i < len; i++) {
      const u = units[i];
      if (u.isDead || u.team !== 'eastern') continue;
      ctx.fillRect(Math.round(u.x * scaleX) - 1, blipY, 2, 3);
    }

    // Camera viewport frame - what's actually on screen right now.
    const halfVisibleW = (viewport.cssW / viewport.camera.zoom) / 2;
    const camLeft = Math.max(0, viewport.camera.x - halfVisibleW) * scaleX;
    const camRight = Math.min(worldW, viewport.camera.x + halfVisibleW) * scaleX;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(camLeft + 0.75, 0.75, Math.max(2, camRight - camLeft - 1.5), h - 1.5);
  }
}
